// AUTHOR: Rahul Choudhary | Whirldata Inc.
// DATE: 22 Aug 2022
// DESCRIPTION:
// -- For users to login to the web-app.
// -- Hooks for username and password capture the values as and when they are entered,
//    loginClicked() method calls the <login> API,
//    gets the idToken in the response, stores in <storage> to be used in header for all other API calls.
// -- Once authenticated, /home page replaces this page in navigation stack.
// -- Below mentioned column ratios are from bootstrap v5 context.

import { Button, Card, Checkbox, Form, Input, message, Typography } from 'antd';
import jwtDecode from 'jwt-decode';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Logo from '../assets/logo.png';
import LogoLong from '../assets/logo_long_new.png';
import ErrorAlert from '../components/ErrorAlert';
import Spinner from '../components/Spinner';
import SuccessAlert from '../components/SuccessAlert';
import { useAppContext } from '../context/TechnologyContext';
import { ApiHandler } from '../services/apiHandler';
import './Login.css';

const Login: FC = () => {
  let navigate = useNavigate(); // Hook for navigation used in loginClicked()

  const { setTechType } = useAppContext();

  const locationParams = useParams();

  // State Hooks
  const [isPasswordVisible, togglePasswordType] = useState(false);
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorAlert, setErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [forgotPasswordActive, setForgotPasswordActive] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState('');
  const [successAlert, setSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [form] = Form.useForm();
  if (!locationParams.replace) localStorage.clear();

  useEffect(() => {
    if (localStorage.getItem('access_token') && !locationParams.replace) {
      navigate('/home', { replace: true });
    }
  }, []);

  // Methods
  const loginClicked = (event: any) => {
    event.preventDefault();
    if (username.trim() !== '' && password.trim() !== '') {
      setLoading(true);
      ApiHandler({ apiName: 'login', params: {}, body: { username: username, password: password } })
        .then((response: any) => {
          const decoded: any = jwtDecode(response.data.access_token);
          localStorage.setItem('access_token', response.data.access_token);
          localStorage.setItem('refresh_token', response.data.refresh_token);
          localStorage.setItem('technology_type', decoded?.technology_type_name ?? 'NO_TECH');
          setTechType(decoded?.technology_type_name ?? 'NO_TECH');
          setLoading(false);
          navigate('/home', { replace: true });
        })
        .catch((error: any) => {
          setLoading(false);
          setErrorMessage(error.data.message);
          setErrorAlert(true);
          message.error(error.data.message);
        });
    }
  };

  const forgotPasswordClicked = (e: any) => {
    e.preventDefault();
    if (email.trim() === '') return;
    setLoading(true);
    ApiHandler({ apiName: 'forgotPassword', params: {}, body: { email: email.trim().toLowerCase() } })
      .then((response: any) => {
        setLoading(false);
        setSuccessMessage('Email sent successfully.');
        setSuccessAlert(true);
        setEmailSent(true);
      })
      .catch((error: any) => {
        setLoading(false);
        setErrorMessage(error.data.message);
        setErrorAlert(true);
      });
  };

  // View
  return forgotPasswordActive ? (
    <div>
      {/* Background overlay image */}
      <div className="background" />
      {/* Login Card */}
      <div className="vertical-center h-100 w-100" style={{ position: 'absolute', justifyContent: 'end' }}>
        <div className="login-card center" style={{ marginRight: '7.3vw' }}>
          <img className="logo" src={Logo} alt="c3-spectra logo"></img>

          <span className="action-title mb-3" style={{ marginTop: '5.5vh' }}>
            Forgot Password
          </span>
          {!emailSent ? (
            <div className="col-wise">
              <span className="action-instructions" style={{ marginBottom: '3.9vh' }}>
                Enter the email address associated with your account and we'll send instructions to reset your password.
              </span>

              <form className="w-100" onSubmit={forgotPasswordClicked}>
                <div className="form-floating w-100" style={{ marginBottom: '1.3vh' }}>
                  <input
                    className="form-control input-box input-text vertical-center"
                    placeholder="Enter your email address"
                    id="emailInput"
                    type={'email'}
                    onInput={(e: any) => {
                      setEmail(e.target.value);
                    }}
                    autoComplete={'off'}
                    value={email}></input>
                  <label className="input-label vertical-center" htmlFor="usernameInput">
                    Email Address
                  </label>
                </div>

                <button type="submit" className="w-100 center primary-action-button" disabled={email === ''}>
                  <span>Send</span>
                </button>
              </form>
            </div>
          ) : (
            <div className="email-confirmation mt-4 mb-4">
              <span>
                Check your inbox for the next steps. If you don't receive an email, and it's not in your spam folder this could mean your account is
                associated with a different address.
              </span>
            </div>
          )}

          <span
            className="interactible-text forgot-password-text mt-4"
            onClick={() => {
              setForgotPasswordActive(false);
              setEmail('');
              setEmailSent(false);
            }}>
            Log In instead?
          </span>
        </div>
      </div>

      <Spinner show={loading} />
      <ErrorAlert
        show={errorAlert}
        onDismiss={() => {
          setErrorAlert(false);
        }}
        message={errorMessage}
      />
      <SuccessAlert
        show={successAlert}
        onDismiss={() => {
          setSuccessAlert(false);
        }}
        message={successMessage}
      />
    </div>
  ) : (
    <div className="d-flex justify-content-center align-items-center w-100" style={{ backgroundColor: '#F5F5F5', height: '100vh' }}>
      <div className="d-flex justify-content-center align-items-center" style={{ position: 'absolute', top: '40px', left: '40px', right: '40px' }}>
        <img src={LogoLong} alt="logo" height={40} />
      </div>
      <Card style={{ width: '472px', padding: '24px 0px' }}>
        <Typography.Title level={2}>Welcome back!</Typography.Title>
        <Form layout="vertical" form={form} onSubmitCapture={(e) => loginClicked(e)} className="mt-4">
          <Form.Item label="Username" name="username">
            <Input size="large" disabled={loading} placeholder="Enter your username" onChange={(e) => setUsername(e.target.value)} />
          </Form.Item>
          <Form.Item label="Password" name="password">
            <Input.Password size="large" disabled={loading} placeholder="Password" onChange={(e) => setPassword(e.target.value)} />
          </Form.Item>
          <div className="d-flex justify-content-between align-items-center">
            <Form.Item>
              <Checkbox>Keep me signed in</Checkbox>
            </Form.Item>
            <Form.Item>
              <Button type="link" onClick={() => setForgotPasswordActive(true)}>
                Forgot your password?
              </Button>
            </Form.Item>
          </div>
          <Form.Item>
            <Button htmlType="submit" type="primary" block size="large" disabled={!username || !password || loading} loading={loading}>
              Sign in
            </Button>
          </Form.Item>
          <Form.Item>
            <Button type="default" block size="large" onClick={() => {}}>
              Sign in with SSO
            </Button>
          </Form.Item>
        </Form>
      </Card>{' '}
    </div>
  );
};

export default Login;
